import { FC } from 'react'
import { useAmountAndCurrency } from '../../../../shared/useAmountAndCurrency'
import { ObjectiveStatus, FullObjectivePaymentFragment, ObjectiveDto, TargetCorrectionPaymentDto, Currency } from '../../../../types'
import { useAuth } from '../../../../context/auth-context'
import { PaymentFooterTemplate } from '../../../../components/custom_components/PaymentFooterTemplate'
import { filterObjectivesByTypeStatusAndRefYear } from '../../okr/util'
import { useCurrency } from '../../../../context/currency-context'
import { retrieveCurrencyConversionRate } from '../../../../shared/currencyConversionUtil'

interface Props {
  objectives: ObjectiveDto[]
  oneOffPayment?: number
  objectivePayment?: FullObjectivePaymentFragment
  targetCorrectionPayment?: TargetCorrectionPaymentDto
}

export const PaymentFooter: FC<Props> = ({ objectives, oneOffPayment, objectivePayment, targetCorrectionPayment }) => {
  const { user } = useAuth()
  const { formatCurrency } = useAmountAndCurrency(user.locale)

  const { currency, currencyConversionData } = useCurrency()

  const toBePaidObjectives = filterObjectivesByTypeStatusAndRefYear(objectives, true, [
    ObjectiveStatus.Approved,
    ObjectiveStatus.PastApproved,
  ])

  const unknownCurrenciesForConversion: Currency[] = [
    ...new Set(
      toBePaidObjectives
        .filter((obj): boolean => {
          if (obj.currency! !== currency) {
            const conversionRate: number | undefined = retrieveCurrencyConversionRate(obj.currency!, currency, currencyConversionData)
            if (!conversionRate) {
              return true
            }
          }
          return false
        })
        .map((obj) => obj.currency!),
    ),
  ]

  const targetBonusToBePaid = Number(
    toBePaidObjectives
      .reduce((sum, obj) => {
        if (obj.currency !== currency) {
          if (!obj.achievedAmount || unknownCurrenciesForConversion.includes(obj.currency!)) {
            return sum
          }
          return (
            sum + Number((obj.achievedAmount * retrieveCurrencyConversionRate(obj.currency!, currency, currencyConversionData)!).toFixed(2))
          )
        }
        return sum + (obj.achievedAmount || 0)
      }, 0)
      .toFixed(2),
  )

  const hasMonetaryTargets = toBePaidObjectives.length > 0

  const oneOffPaymentResult = oneOffPayment ? oneOffPayment : 0

  const targetCorrectionPaymentResult = targetCorrectionPayment ? targetCorrectionPayment.resultAmount : 0

  const manualDeduction = objectivePayment && objectivePayment.deduction ? objectivePayment.deduction : 0

  return (
    <PaymentFooterTemplate
      formatCurrencyCallback={(amount: number) => formatCurrency(amount, currency, true)}
      achievedTargetBonus={targetBonusToBePaid}
      manualDeduction={manualDeduction}
      oneOffPayment={!!oneOffPaymentResult ? oneOffPayment : undefined}
      targetCorrectionPayment={!!targetCorrectionPaymentResult ? targetCorrectionPaymentResult : undefined}
      hasMonetaryTargets={hasMonetaryTargets}
      unknownCurrenciesForConversion={unknownCurrenciesForConversion}
    />
  )
}
